import { Button, DownloadIcon } from "@rothoblaas/design-system";
import { FC, memo, useContext, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "../../../components/ui/Icon";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import useDropdownMenu from "../../../hooks/useDropdownMenu";
import useI18n from "../../../hooks/useTranslations";
import { getConcreteResinReportFileName, useConcreteResinReportApi } from "../../../http/concreteResin/useReport";
import { getUILanguages, Language } from "../../../types";
import { loadPublicImageAsBase64, loadPublicSvgAsPngBase64 } from "../../../utils";
import styles from "./ConcreteResinPdfExportButton.module.css";

// The page's own pictures, which the report shows too (its other pictures are the server's): the
// preview's hole with its measurements (ConcreteResinPreview) and how to inject the resin
// (ConcreteResinDetailInstructionsAndNotes).
const HOLE_GEOMETRY_IMAGE_URL = "/concreteResin/instructions-preview-resin-quantity.png";
const APPLICATION_IMAGE_URL = "/concreteResin/instructions-resin-application.svg";
// The SVG's PNG width for the report, which prints it 40% of the A4 page wide: about 300 dpi.
const APPLICATION_IMAGE_WIDTH = 1000;

type ConcreteResinPdfExportButtonProps = {
  calculationId: string;
  /** Whether the calculation has saved entries, which the report is made of */
  hasEntries: boolean;
  /** Disables it, e.g. until the calculation's settings are loaded */
  disabled?: boolean;
  /** Module/calculation/project names used to build the downloaded PDF's filename - each falls back
   * to empty/generic when unset. */
  moduleName?: string | null;
  calculationName?: string | null;
  projectName?: string | null;
};

// Downloads the PDF report of the saved calculations in the language picked from its menu, like the
// deck designer's PDF button (BillOfQuantitiesCard). The server builds the report from the saved
// entries, so it's disabled until there's one.
const ConcreteResinPdfExportButton: FC<ConcreteResinPdfExportButtonProps> = ({
  calculationId,
  hasEntries,
  disabled = false,
  moduleName,
  calculationName,
  projectName,
}) => {
  const i18n = useI18n();
  const resinTranslation = i18n.translation.concreteResinModule;
  const dispatch = useContext(NotificationDispatch);
  const languages = getUILanguages(i18n.translation);
  const { downloadConcreteResinReport } = useConcreteResinReportApi();

  const [isGenerating, setIsGenerating] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = useDropdownMenu(isMenuOpen, () => setIsMenuOpen(false));

  const download = async (language: Language) => {
    setIsMenuOpen(false);
    setIsGenerating(true);
    try {
      const [holeGeometryImage, applicationImage] = await Promise.all([
        loadPublicImageAsBase64(HOLE_GEOMETRY_IMAGE_URL),
        loadPublicSvgAsPngBase64(APPLICATION_IMAGE_URL, APPLICATION_IMAGE_WIDTH),
      ]);
      const today = new Date();
      const pad = (value: number) => String(value).padStart(2, "0");
      const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
      await downloadConcreteResinReport(
        calculationId,
        { language: language.value, date, holeGeometryImage, applicationImage },
        getConcreteResinReportFileName(moduleName, calculationName, projectName),
      );
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    // Context menu listing all 5 languages - pick one to download the PDF in that language.
    <div ref={triggerRef} className={styles.menuWrapper}>
      <Button
        type="button"
        variant="primary"
        className={styles.pdfButton}
        disabled={disabled || !hasEntries || isGenerating}
        isLoading={isGenerating}
        onClick={() => setIsMenuOpen((open) => !open)}
      >
        <Icon glyph={DownloadIcon} />
        {resinTranslation.downloadPdf}
      </Button>
      {/* Only while there's nothing to report, not while a PDF is being generated. */}
      {!hasEntries && (
        <span className={styles.disabledTooltip}>{resinTranslation.downloadPdfNoCalculationsSavedHint}</span>
      )}
      {/* Portaled, as the design-system ContextMenu: no card's overflow can clip it. */}
      {isMenuOpen &&
        createPortal(
          <ul ref={menuRef} className={styles.languageMenu} style={menuStyle}>
            {languages.map((language) => (
              <li key={language.value} onClick={() => download(language)}>
                {language.display}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default memo(ConcreteResinPdfExportButton);
